import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal, RevealLines } from './Reveal';
import { IMG } from '../data/site';

const BULLETS = [
  { t: 'Bar mobile élégant', d: 'Structure travaillée, éclairage doux, présence chic.' },
  { t: 'Installation rapide', d: 'Prêt à servir sous 45 minutes, sans compromis.' },
  { t: 'Personnalisable', d: 'Aux couleurs, au thème, au nom de votre événement.' },
  { t: 'Intérieur / extérieur', d: 'Adapté aux terrasses, jardins, salles et rooftops.' },
  { t: 'Grande capacité', d: 'Un service maîtrisé, sans jamais faire attendre.' },
];

export default function MobileBar() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section id="bar" ref={ref} className="relative py-32 md:py-48 overflow-hidden" data-testid="bar-section">
      <div className="px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
          <Reveal className="col-span-12 md:col-span-3">
            <div className="flex md:flex-col items-baseline md:items-start gap-4">
              <span className="font-mono text-gold text-sm tracking-[0.3em]">03 —</span>
              <span className="overline">Le bar mobile</span>
            </div>
          </Reveal>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-serif leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(2.5rem, 6vw, 6rem)' }}>
              <RevealLines lines={['Un objet de scène.', <span key="i" className="italic text-char/50">Une pièce maîtresse.</span>]} />
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {/* Big image left */}
          <Reveal className="col-span-12 lg:col-span-7">
            <div className="relative aspect-[4/5] lg:aspect-[3/4] overflow-hidden">
              <motion.img
                style={{ y, objectPosition: '50% 12%' }}
                src={IMG.mobileBar}
                alt="Bar mobile Ô Fil des Verres — bar en bois avec enseigne signature"
                className="w-full h-[115%] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="overline text-white/80">Installation extérieure</div>
                  <div className="font-serif text-2xl mt-1 text-white">Le bar signature — jardin privé</div>
                </div>
                <span className="font-mono text-gold text-xs">01 / 06</span>
              </div>
            </div>
          </Reveal>

          {/* Right column: features */}
          <div className="col-span-12 lg:col-span-5 flex flex-col justify-between">
            <div className="space-y-6">
              {BULLETS.map((b, i) => (
                <Reveal key={b.t} delay={i * 0.06}>
                  <div className="border-b border-char/10 pb-5">
                    <div className="flex items-baseline justify-between">
                      <div className="font-serif text-2xl md:text-3xl">{b.t}</div>
                      <span className="font-mono text-gold text-xs">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <div className="text-char/60 text-sm mt-2">{b.d}</div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="relative aspect-[4/5] overflow-hidden group">
                  <img src={IMG.outdoor} alt="La Grange de Javon — installation extérieure avec piscine" className="w-full h-full object-cover object-[30%_25%] group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                  <span className="absolute bottom-3 left-3 overline text-white/90">La Grange de Javon</span>
                </div>
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={IMG.pour} alt="Cocktail aux fruits rouges — création Ô Fil des Verres" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" loading="lazy" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
